import React, {
  forwardRef, type MouseEvent, type Ref,
} from 'react';
import { Panel, Link } from '@printdeal/ui-library';
import { AutocompleteApi, AutocompleteCollection } from '@algolia/autocomplete-core';
import { AutocompleteQuerySuggestionsHit } from '@algolia/autocomplete-plugin-query-suggestions/dist/esm/types';
import { useReadyForCypress } from '../../../../hooks/useReadyForCypress';

export type AutocompleteApiWithSuggestions =
  AutocompleteApi<AutocompleteQuerySuggestionsHit, Event, MouseEvent<HTMLButtonElement>>;

interface SearchSuggestionProps {
  autocompleteApi: AutocompleteApiWithSuggestions;
  collections: Array<AutocompleteCollection<AutocompleteQuerySuggestionsHit>>;
  ref?: Ref<HTMLDivElement>;
}

const SearchSuggestions = forwardRef<HTMLDivElement, SearchSuggestionProps>(
  ({ autocompleteApi, collections = [] }, ref: Ref<HTMLDivElement>) => {
    const readyForCypressId = useReadyForCypress('search-suggestions');

    if (!collections.length) return null;

    return (
      <div className="pd-absolute pd-inset-x-5 pd-z-10 pd-pt-2" data-cy={readyForCypressId} ref={ref}>
        <Panel className="pd-flex pd-flex-col pd-gap-3 pd-shadow">
          {collections.map(({ source, items }) => {
            const props = autocompleteApi.getListProps();

            if (!items.length) return null;

            return (
              <ul key={source.sourceId} className="pd-flex pd-flex-col pd-items-stretch pd-gap-3" {...props}>
                {items.map((item) => {
                  const itemProps = autocompleteApi.getItemProps({
                    item,
                    source,
                  });

                  if (source.sourceId === 'RecentSearches') {
                    return (
                      <li key={item.query} className="pd-flex" data-cy="search-suggestion">
                        <Link
                          as="button"
                          type="button"
                          variant="secondary"
                          iconBefore="clock"
                          className="pd-w-full"
                          {...itemProps}
                        >
                          {item.query}
                        </Link>
                      </li>
                    );
                  }

                  return (
                    <li key={item.objectID} className="pd-flex" data-cy="search-suggestion">
                      <Link
                        as="button"
                        type="button"
                        variant="secondary"
                        className="pd-w-full"
                        {...itemProps}
                      >
                        {item.query}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            );
          })}
        </Panel>
      </div>
    );
  },
);

export default SearchSuggestions;
